import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { TransferState } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { catchError, map, take, timeout } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { DealerSiteProfile, MOTORBEST_DEFAULT } from './dealer-site.model';
import {
  DEALER_ID_TOKEN,
  DEALER_SITE_KEY,
  DEALER_ID_KEY,
} from './dealer-id.token';
import { ConsentService } from '@core/consent/consent.service';

@Injectable({ providedIn: 'root' })
export class TenantService {
  readonly dealerId: string | null;
  readonly isDealer: boolean;

  private _profile$ = new ReplaySubject<DealerSiteProfile | null>(1);
  private _initDone = false;

  constructor(
    @Optional() @Inject(DEALER_ID_TOKEN) dealerIdFromToken: string | null,
    @Inject(PLATFORM_ID) private _platformId: object,
    @Optional() @Inject(DOCUMENT) private _document: Document,
    private _transferState: TransferState,
    private _http: HttpClient,
    private _consent: ConsentService,
  ) {
    // Prioridade: token SSR → TransferState (browser hydration) → null (Motorbest)
    const transferredId = this._transferState.get(DEALER_ID_KEY, null);
    this.dealerId = dealerIdFromToken ?? transferredId ?? null;
    this.isDealer = !!this.dealerId;
  }

  get profile$(): Observable<DealerSiteProfile | null> {
    return this._profile$.asObservable();
  }

  get profile(): DealerSiteProfile | null {
    // Snapshot — só disponível após init()
    return (this._profile$ as any)._buffer?.[0] ?? null;
  }

  /** Mostrar preços? Default true (só esconde se explicitamente false). */
  get showPrices(): boolean {
    return this.profile?.dealerSite?.showPrices !== false;
  }

  /**
   * Idempotente — pode ser chamado pelo guard e pelo resolver sem double-fetch.
   * SSR: faz fetch → guarda no TransferState.
   * Browser: lê do TransferState → sem fetch.
   * Sem dealer: emite null imediatamente.
   */
  init(): Observable<DealerSiteProfile | null> {
    if (this._initDone) {
      return this._profile$.pipe(take(1));
    }
    this._initDone = true;

    // Gating de consent (browser-only) — injecta scripts só após opt-in.
    this._wireConsentGating();

    // Sem dealer — portal Motorbest
    if (!this.isDealer || !this.dealerId) {
      this._profile$.next(null);
      return of(null);
    }

    // Browser hydration — profile já vem do SSR via TransferState
    if (
      isPlatformBrowser(this._platformId) &&
      this._transferState.hasKey(DEALER_SITE_KEY)
    ) {
      const profile = this._transferState.get(DEALER_SITE_KEY, null);
      this._transferState.remove(DEALER_SITE_KEY);
      this._transferState.remove(DEALER_ID_KEY);
      this._profile$.next(profile);
      this._applyBodyClass(profile);
      this._applyBranding(profile);
      this._applyFavicon(profile);
      return of(profile);
    }

    // Fetch (SSR ou ng serve sem TransferState)
    return this._http
      .get<{ data: DealerSiteProfile }>(
        `${environment.api}/sellers/${this.dealerId}/public-profile`,
      )
      .pipe(
        timeout(5000),
        map((res) => {
          const profile = res?.data ?? null;
          if (isPlatformServer(this._platformId)) {
            this._transferState.set(DEALER_SITE_KEY, profile);
            this._transferState.set(DEALER_ID_KEY, this.dealerId);
          }
          this._profile$.next(profile);
          this._applyBodyClass(profile);
          this._applyBranding(profile);
          this._applyFavicon(profile);
          // NOTA: analytics/marketing NÃO são injectados aqui (nem no SSR).
          // A injecção é browser-only e gated por consent — ver _wireConsentGating.
          return profile;
        }),
        catchError(() => {
          this._profile$.next(null);
          return of(null);
        }),
      );
  }

  /** Aplica a classe de tema no <body> — browser e SSR */
  private _applyBodyClass(profile: DealerSiteProfile | null): void {
    if (!this._document) return;
    const template = profile?.dealerSite?.template;
    if (!template || template === 'motorbest') return;
    if (isPlatformBrowser(this._platformId)) {
      this._document.body.classList.forEach((cls) => {
        if (cls.startsWith('theme-')) this._document.body.classList.remove(cls);
      });
    }
    this._document.body.classList.add(`theme-${template}`);
  }

  /**
   * Aplica as cores do bloco `branding` como overrides inline das CSS vars do tema
   * autodepoca (definidas em `body.theme-autodepoca`). Inline vence a stylesheet.
   * SSR-aware: no SSR fica serializado no body; idempotente na hidratação.
   */
  private _applyBranding(profile: DealerSiteProfile | null): void {
    if (!this._document) return;
    const ds = profile?.dealerSite;
    if (!ds || ds.template === 'motorbest') return;
    const b = ds.branding;
    const body = this._document.body;
    if (!b || !body) return;

    const set = (name: string, value?: string) => {
      if (value) body.style.setProperty(name, value);
    };
    // primaryColor → dourado principal (CTAs, labels, nav activo) + borda derivada
    set('--ade-gold-meta', b.primaryColor);
    // header/menu (activo + hover) usam --color-gold-primary
    set('--color-gold-primary', b.primaryColor);
    if (b.primaryColor) {
      body.style.setProperty(
        '--ade-gold-border',
        this._hexToRgba(b.primaryColor, 0.35),
      );
    }
    // secondaryColor → títulos de secção (fallback ao primary)
    set('--ade-gold-title', b.secondaryColor || b.primaryColor);
    // accentColor → badges (VENDIDO / RESTAURO)
    set('--ade-badge', b.accentColor);
  }

  private _hexToRgba(hex: string, alpha: number): string {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return hex;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const bl = parseInt(m[3], 16);
    return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
  }

  /** Aplica o favicon do dealer — cobre todos os templates */
  private _applyFavicon(profile: DealerSiteProfile | null): void {
    if (!this._document || !isPlatformBrowser(this._platformId)) return;
    const faviconUrl = profile?.dealerSite?.branding?.favicon?.url;
    if (!faviconUrl) return;
    const url = faviconUrl.startsWith('http')
      ? faviconUrl
      : `${environment.assets}${faviconUrl}`;
    this._document
      .querySelectorAll<HTMLLinkElement>(
        'link[rel="icon"], link[rel="apple-touch-icon"]',
      )
      .forEach((el) => {
        el.href = url;
      });
  }

  /**
   * Liga o gating de consent (browser-only). Injecta os scripts do dealer só
   * depois do opt-in da categoria respectiva (prior-blocking). Idempotente via
   * atributos de dedup nos nós injectados.
   */
  private _consentWired = false;
  private _wireConsentGating(): void {
    if (!isPlatformBrowser(this._platformId) || this._consentWired) return;
    this._consentWired = true;
    combineLatest([this.profile$, this._consent.state$]).subscribe(
      ([profile, state]) => {
        if (!profile || !state) return; // motorbest (profile null) ou sem decisão → nada
        if (state.analytics) this.injectAnalytics(profile);
        if (state.marketing) this.injectMarketing(profile);
      },
    );
  }

  /**
   * Categoria MARKETING — FB Pixel + scripts custom do dealer (HTML bruto).
   * Browser-only. Guard `[data-ade-marketing]` evita dupla injecção.
   */
  injectMarketing(profile: DealerSiteProfile | null): void {
    if (!isPlatformBrowser(this._platformId) || !this._document) return;
    const ds = profile?.dealerSite;
    if (!ds || ds.template === 'motorbest') return;
    if (this._document.querySelector('[data-ade-marketing]')) return;

    const head = this._document.head;
    const body = this._document.body;
    const a = ds.analytics;

    // Scripts custom do dealer (ex.: CookieYes próprio) — tudo tratado como marketing.
    if (a?.customHeadScripts && head) {
      this._injectRawHtml(head, a.customHeadScripts, 'data-ade-marketing');
    }
    if (a?.customBodyScripts && body) {
      this._injectRawHtml(body, a.customBodyScripts, 'data-ade-marketing');
    }

    // Meta (Facebook) Pixel.
    const fb = a?.facebookPixelId?.trim();
    if (fb && head) {
      this._appendInlineScript(
        head,
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?` +
          `n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;` +
          `n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
          `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}` +
          `(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
          `fbq('init','${fb}');fbq('track','PageView');`,
        'data-ade-marketing',
      );
      if (body) {
        const ns = this._document.createElement('noscript');
        ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${fb}&ev=PageView&noscript=1"/>`;
        ns.setAttribute('data-ade-marketing', 'fb');
        body.appendChild(ns);
      }
    }
  }

  /** Parse de HTML bruto → nós reais (scripts recriados para executarem no browser). */
  private _injectRawHtml(
    target: HTMLElement,
    html: string,
    markerAttr = 'data-ade-scripts',
  ): void {
    const container = this._document.createElement('div');
    container.innerHTML = html;
    Array.from(container.childNodes).forEach((node) => {
      let out: Node = node;
      if (node.nodeName === 'SCRIPT') {
        const src = node as HTMLScriptElement;
        const script = this._document.createElement('script');
        Array.from(src.attributes).forEach((a) =>
          script.setAttribute(a.name, a.value),
        );
        script.text = src.textContent || '';
        out = script;
      }
      if (out.nodeType === 1) {
        (out as HTMLElement).setAttribute(markerAttr, '');
      }
      target.appendChild(out);
    });
  }

  /**
   * Categoria ANALYTICS — GTM + GA4 do dealer. Browser-only, gated por consent.
   * Só para dealers (nunca motorbest). Guard `[data-ade-analytics]` evita duplicação.
   */
  injectAnalytics(profile: DealerSiteProfile | null): void {
    if (!isPlatformBrowser(this._platformId) || !this._document) return;
    const ds = profile?.dealerSite;
    if (!ds || ds.template === 'motorbest') return;
    if (this._document.querySelector('[data-ade-analytics]')) return;

    const head = this._document.head;
    const body = this._document.body;
    // Pixels vêm do bloco `analytics` (fonte canónica).
    const a = ds.analytics;
    const gtm = a?.googleTagManagerId?.trim();
    const ga = a?.googleAnalyticsId?.trim();

    if (gtm && head) {
      this._appendInlineScript(
        head,
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
          `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;` +
          `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})` +
          `(window,document,'script','dataLayer','${gtm}');`,
      );
      if (body) {
        const ns = this._document.createElement('noscript');
        ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        ns.setAttribute('data-ade-analytics', 'gtm');
        body.insertBefore(ns, body.firstChild);
      }
    }

    if (ga && head) {
      this._appendExternalScript(
        head,
        `https://www.googletagmanager.com/gtag/js?id=${ga}`,
      );
      this._appendInlineScript(
        head,
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
          `gtag('js',new Date());gtag('config','${ga}');`,
      );
    }
  }

  private _appendInlineScript(
    target: HTMLElement,
    code: string,
    markerAttr = 'data-ade-analytics',
  ): void {
    const s = this._document.createElement('script');
    s.text = code;
    s.setAttribute(markerAttr, 'inline');
    target.appendChild(s);
  }

  private _appendExternalScript(
    target: HTMLElement,
    src: string,
    markerAttr = 'data-ade-analytics',
  ): void {
    const s = this._document.createElement('script');
    s.src = src;
    s.async = true;
    s.setAttribute(markerAttr, 'ext');
    target.appendChild(s);
  }
}
